/**
 * Category Chart Component
 *
 * Displays category breakdown as a pie/donut chart using Recharts.
 * Shows revenue distribution across product categories.
 */

import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import type { CategoryRevenue, CategoryPercentage } from "@/shared/types/sales-analytics";

interface CategoryChartProps {
  data: CategoryRevenue[] | CategoryPercentage[];
  title?: string;
  description?: string;
}

// Color palette for categories (12 distinct colors)
const COLORS = [
  '#0088FE', // Blue
  '#00C49F', // Teal
  '#FFBB28', // Yellow
  '#FF8042', // Orange
  '#8884D8', // Purple
  '#82CA9D', // Green
  '#FFC658', // Gold
  '#FF6B6B', // Red
  '#4ECDC4', // Cyan
  '#95E1D3', // Mint
  '#F38181', // Pink
  '#AA96DA', // Lavender
];

// Donut + label geometry. Labels stack vertically per side with a min gap so
// adjacent tiny slices never overlap. Leader lines visually connect each label
// back to its slice.
const OUTER_RADIUS = 100;
const INNER_RADIUS = 60;
const LABEL_RADIAL_OFFSET = 18;
const LABEL_TEXT_PAD = 4;
const MIN_LABEL_GAP = 16;
const RADIAN = Math.PI / 180;

interface LabelSlot {
  cos: number;
  sin: number;
  side: 'left' | 'right';
  baseY: number;
  adjustedY: number;
}

function buildLabelLayout(sortedData: { revenue: number }[]): Map<number, LabelSlot> {
  const total = sortedData.reduce((s, d) => s + d.revenue, 0) || 1;
  let cum = 0;
  const slots: Array<LabelSlot & { index: number }> = sortedData.map((d, index) => {
    const startFrac = cum / total;
    cum += d.revenue;
    const endFrac = cum / total;
    const midAngle = ((startFrac + endFrac) / 2) * 360; // math: 0=right, CCW
    const cos = Math.cos(midAngle * RADIAN);
    const sin = Math.sin(midAngle * RADIAN);
    // Recharts uses screen coords (y-down). Flip y so positive sin maps to "up"
    // first, then translate to screen via cy + screenY.
    const screenY = -sin * (OUTER_RADIUS + LABEL_RADIAL_OFFSET);
    const side: 'left' | 'right' = cos >= 0 ? 'right' : 'left';
    return { index, cos, sin, side, baseY: screenY, adjustedY: screenY };
  });

  (['left', 'right'] as const).forEach((side) => {
    const onSide = slots
      .filter((it) => it.side === side)
      .sort((a, b) => a.baseY - b.baseY);
    let prevY = -Infinity;
    for (const it of onSide) {
      const targetY = Math.max(it.baseY, prevY + MIN_LABEL_GAP);
      it.adjustedY = targetY;
      prevY = targetY;
    }
  });

  return new Map(slots.map((it) => [it.index, it]));
}

/**
 * Custom tooltip renderer
 */
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-semibold">{data.categoryName}</p>
        <p className="text-sm text-muted-foreground">
          Revenue: ฿{data.revenue.toLocaleString()}
        </p>
        <p className="text-sm text-muted-foreground">
          Percentage: {data.percentage.toFixed(2)}%
        </p>
        <p className="text-sm text-muted-foreground">
          Projects: {'projectCount' in data ? data.projectCount : 'N/A'}
        </p>

        {/* Show growth data if available */}
        {data.growth && (
          <>
            <div className="border-t border-border my-2"></div>
            <p className="text-xs font-semibold text-muted-foreground">Growth vs Previous Period:</p>
            <p className="text-sm">
              Revenue:{' '}
              <span className={data.growth.revenue.changePercentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                {data.growth.revenue.changePercentage >= 0 ? '+' : ''}
                {data.growth.revenue.changePercentage.toFixed(1)}%
              </span>
              {' '}(฿{data.growth.revenue.changeAmount.toLocaleString()})
            </p>
            <p className="text-sm">
              Projects:{' '}
              <span className={data.growth.projectCount.changePercentage >= 0 ? 'text-green-600' : 'text-red-600'}>
                {data.growth.projectCount.changePercentage >= 0 ? '+' : ''}
                {data.growth.projectCount.changePercentage.toFixed(1)}%
              </span>
              {' '}({data.growth.projectCount.changeAmount >= 0 ? '+' : ''}
              {data.growth.projectCount.changeAmount})
            </p>
          </>
        )}
      </div>
    );
  }
  return null;
};

export function CategoryChart({ data, title = "Category Breakdown", description }: CategoryChartProps) {
  // Sort by revenue descending
  const sortedData = useMemo(
    () => [...data].sort((a, b) => b.revenue - a.revenue),
    [data]
  );

  const labelLayout = useMemo(() => buildLabelLayout(sortedData), [sortedData]);

  const renderLabel = (props: any) => {
    const { cx, cy, index } = props;
    const slot = labelLayout.get(index);
    if (!slot) return null;
    const cat = sortedData[index] as any;

    const sliceX = cx + OUTER_RADIUS * slot.cos;
    const sliceY = cy - OUTER_RADIUS * slot.sin;
    const bendX = cx + (OUTER_RADIUS + LABEL_RADIAL_OFFSET) * slot.cos;
    const labelY = cy + slot.adjustedY;
    const textX = bendX + (slot.side === 'right' ? LABEL_TEXT_PAD : -LABEL_TEXT_PAD);

    const color = COLORS[index % COLORS.length];

    return (
      <g>
        <polyline
          points={`${sliceX},${sliceY} ${bendX},${labelY} ${textX},${labelY}`}
          stroke={color}
          strokeWidth={1}
          fill="none"
        />
        <text
          x={textX}
          y={labelY}
          dy="0.35em"
          textAnchor={slot.side === 'right' ? 'start' : 'end'}
          fill={color}
          fontSize={12}
        >
          {`${cat.categoryName}: ${cat.percentage.toFixed(1)}%`}
        </text>
      </g>
    );
  };

  if (sortedData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No category data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={340}>
          <PieChart margin={{ top: 24, right: 96, bottom: 24, left: 96 }}>
            <Pie
              data={sortedData as any}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderLabel}
              outerRadius={OUTER_RADIUS}
              innerRadius={INNER_RADIUS}
              fill="#8884d8"
              dataKey="revenue"
              nameKey="categoryName"
              isAnimationActive={false}
            >
              {sortedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry: any) => {
                const data = entry.payload;
                return `${data.categoryName} (${data.percentage.toFixed(1)}%)`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Category details table */}
        <div className="mt-4 space-y-2">
          {sortedData.slice(0, 5).map((category, index) => (
            <div
              key={category.category}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="font-medium">{category.categoryName}</span>
              </div>
              <div className="text-right">
                <div className="font-semibold">฿{category.revenue.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">
                  {category.percentage.toFixed(1)}%
                  {'projectCount' in category ? ` • ${category.projectCount} projects` : ''}

                  {/* Show growth badge if available */}
                  {'growth' in category && category.growth && (
                    <span className={`ml-2 ${
                      category.growth.revenue.changePercentage >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {category.growth.revenue.changePercentage >= 0 ? '↑' : '↓'}
                      {Math.abs(category.growth.revenue.changePercentage).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
